<script>
import {
  CdrButton,
  CdrText,
} from '@rei/cedar';
import EaPicture from '../../../components/eaPicture.vue';

export default {
  name: 'EditorsChoiceAwardRecent',
  components: {
    CdrText,
    CdrButton,
    EaPicture,
  },
  props: {
    awardWinners: {
      type: Array,
      default: null,
    },
  },
  data: () => ({
    baseClassName: 'ea-editors-choice-award-recent',
  }),
  computed: {
    backgroundStripingClass() {
      const evenOddCheck = this.awardWinners.length % 2;
      const classEnding = evenOddCheck === 0
        ? '__product_count_even' : '__product_count_odd';
      return this.baseClassName + classEnding;
    },
  },
  methods: {
    shortName(name) {
      return name.splice(0, 16);
    },
    getAltText(award) {
      const alt = award.heroImageInfos.find((x) => x.key === 'recent-winner-square' || x.key === 'recent-winner-horizontal').heroImageInfo.altText;
      return alt || '';
    },
  },
};
</script>

<template>
  <section>
    <div
      v-for="productAward in awardWinners"
      :key="productAward.articleId"
      :class="`${baseClassName} ${backgroundStripingClass}`"
    >
      <div :class="`${baseClassName}__container`">
        <div :class="`${baseClassName}__product_details`">
          <h3 :class="`${baseClassName}__product_name`">
            {{ productAward.productName || '' }}
          </h3>
          <ea-picture
            :class="`${baseClassName}__product_image`"
            :alt-text="getAltText(productAward)"
            :lg-src="productAward.heroImageInfos[0].heroImageInfo.url"
          />
          <div :class="`${baseClassName}__product_description`">
            <cdr-text :class="`${baseClassName}__product_description_short`">
              {{ productAward.shortDescription }}
            </cdr-text>
            <cdr-text :class="`${baseClassName}__product_description_long`">
              {{ productAward.description }}
            </cdr-text>
            <div :class="`${baseClassName}__product_links`">
              <cdr-button
                v-if="productAward.reviewLink"
                :class="`${baseClassName}__link`"
                :href="productAward.reviewLink"
                :data-analytics-id="`recent winners_read full review_${productAward.productName}`"
                tag="a"
                modifier="secondary"
              >
                Read Full Review
              </cdr-button>
              <cdr-button
                v-if="productAward.productLink"
                :class="`${baseClassName}__link`"
                :href="productAward.productLink"
                :data-analytics-id="`recent winners_shop now_${productAward.productName}`"
                tag="a"
                modifier="primary"
              >
                Shop Now
              </cdr-button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style lang="scss">
@import '@rei/cdr-tokens/dist/rei-dot-com/scss/cdr-tokens.scss';

$baseClassName: 'ea-editors-choice-award-recent';

.#{$baseClassName} {
  @include cdr-sm-mq-up {
    &.#{$baseClassName}__product_count_odd:nth-child(odd) {
        background-color: $cdr-color-background-primary;
    }
    &.#{$baseClassName}__product_count_even:nth-child(even) {
        background-color: $cdr-color-background-primary;
      }
  }

  .#{$baseClassName}__container {
    @include cdr-container;
  }

  .#{$baseClassName}__product_name {
    margin: 0;
    padding: 0;
  }

  @include cdr-md-mq-down {
    .#{$baseClassName}__product_name {
      flex: 1 1 100%;
      order: 1;
    }
    .#{$baseClassName}__product_description {
      flex: 1 1 100%;
      order: 2;
    }
    .#{$baseClassName}__product_image {
      flex: 1 1 100%;
      order: 0;
      height: 45vw;
    }
  }

  @include cdr-xs-mq-only {
    padding: $cdr-space-one-x;

    .#{$baseClassName}__product_name {
      flex: 1 1 100%;
      order: 1;
      margin: 0 $cdr-space-one-x;
      @include cdr-xs-mq-only {
        margin-top: $cdr-space-quarter-x;
      }
    }
    .#{$baseClassName}__product_description {
      flex: 1 1 100%;
      order: 2;
      margin: $cdr-space-one-x;

      @include cdr-xs-mq-only {
        margin-top: 0;
      }
    }

    .#{$baseClassName}__product_details {
      gap: $cdr-space-three-quarter-x;
      background-color: $cdr-color-background-primary;
      border-radius: $cdr-radius-softer;
      overflow: hidden;
    }
    .#{$baseClassName}__product_links {
      flex-direction: column;
    }
  }

  @include cdr-sm-mq-only {
    padding: $cdr-space-two-x $cdr-space-three-x;
    .#{$baseClassName}__product_details {
      gap: $cdr-space-two-x;
    }
    .#{$baseClassName}__product_name {
      flex: 0 0 100%;
      order: 0;
    }

    .#{$baseClassName}__product_links {
      order: 3;
      flex-direction: row;
      margin: $cdr-space-one-x 0 $cdr-space-three-x 0
    }

  }

  @include cdr-md-mq-only {
      .#{$baseClassName}__product_image {
        order: 1;
        flex: 1 1 40%;
      }
      .#{$baseClassName}__product_description {
        order: 2;
        flex: 1 1 40%;
      }
  }

  @include cdr-md-mq-up {
    padding: $cdr-space-three-x $cdr-space-four-x;
    .#{$baseClassName}__product_name {
      order: 0;
      flex: 0 0 100%;
    }
    .#{$baseClassName}__product_details {
      gap: $cdr-space-two-x $cdr-space-four-x;
    }

    .#{$baseClassName}__product_description {
      margin-top: $cdr-space-three-x;
      flex: 1 1 40%;
      order: 1;
    }

    .#{$baseClassName}__product_image {
      order: 2;
      flex: 1 1 50%;
    }
    .#{$baseClassName}__product_links {
      flex-direction: row;
      margin: $cdr-space-one-x 0 $cdr-space-three-x 0
    }

    &:nth-child(odd) {
      .#{$baseClassName}__product_description {
        order: 2;
      }
      .#{$baseClassName}__product_image {
        order: 1;
      }
    }
  }
  @include cdr-lg-mq-up {
    padding: $cdr-space-three-x $cdr-space-four-x;
    .#{$baseClassName}__product_details {
      gap: $cdr-space-two-x calc(#{$cdr-space-three-x} * 2);
    }

    .#{$baseClassName}__product_description {
      margin-top: $cdr-space-one-x * 5;
    }
  }

  .#{$baseClassName}__product_details {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;

    .#{$baseClassName}__product_description {
      display: flex;
      flex-direction: column;
      gap: $cdr-space-one-and-a-half-x;
    }
  }

  .#{$baseClassName}__product_links {
    display: flex;
    justify-content:space-between;
    gap: $cdr-space-one-x;
  }

 .#{$baseClassName}__link {
    flex: 1 1 50%;
    justify-content: center;
    white-space: nowrap;
  }

}

.#{$baseClassName}__product_description_short {
  margin: $cdr-space-one-and-a-half-x 0;
}
.#{$baseClassName}__product_description_long {
  margin: $cdr-space-one-x 0;
}

.#{$baseClassName} {
  @include cdr-xs-mq-only {
    background-color: $cdr-color-background-secondary;

    .#{$baseClassName}__product_name {
      @include cdr-text-heading-serif-500;
    }
    .#{$baseClassName}__product_description_short {
      display: none;
    }
    .#{$baseClassName}__product_description_long {
      @include cdr-text-body-300;
    }
  }
  @include cdr-sm-mq-only {
    .#{$baseClassName}__product_name {
        @include cdr-text-heading-display-1000;
    }
    .#{$baseClassName}__product_description_short {
      @include cdr-text-utility-sans-600;
    }
    .#{$baseClassName}__product_description_long {
      @include cdr-text-body-300;
    }
  }
  @include cdr-md-mq-only {
    .#{$baseClassName}__product_name {
        @include cdr-text-heading-display-1000;
    }
    .#{$baseClassName}__product_description_short {
      @include cdr-text-utility-sans-500;
      font-weight: 500;
      line-height: 3.6rem;
    }
    .#{$baseClassName}__product_description_long {
      @include cdr-text-body-400;
    }
  }
  @include cdr-lg-mq-only {
    .#{$baseClassName}__product_name {
        @include cdr-text-heading-display-1200;
    }
    .#{$baseClassName}__product_description_short {
      @include cdr-text-utility-sans-500;
      font-weight: 500;
      line-height: 3.6rem;
    }
    .#{$baseClassName}__product_description_long {
      @include cdr-text-body-400;
    }
  }
}
</style>
